/**
 * SuperAdminSidebar — Cockpit sidebar shown when SuperAdmin browses
 * /admin or /prospects. Replaces the standard PremiumSidebar in those views.
 */
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  TrendingUp,
  Rocket,
  Building2,
  Clock,
  CreditCard,
  Users,
  Sparkles,
  Brain,
  Layers,
  Database,
  Settings,
  Shield,
  MessageSquare,
  LogOut,
  ArrowLeft,
} from 'lucide-react';

const TAB_ITEMS = [
  { tab: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { tab: 'lean-insights', label: 'Insights Lean', icon: TrendingUp },
  { tab: 'growth', label: 'Croissance', icon: Rocket, highlight: true },
  { tab: 'companies', label: 'Entreprises', icon: Building2 },
  { tab: 'expirations', label: 'Expirations', icon: Clock },
  { tab: 'billing', label: 'Facturation', icon: CreditCard },
  { tab: 'users', label: 'Utilisateurs', icon: Users },
  { tab: 'ai-credits', label: 'Crédits IA', icon: Sparkles },
  { tab: 'ai', label: 'Supervision IA', icon: Brain },
  { tab: 'modules', label: 'Modules', icon: Layers },
  { tab: 'backup', label: 'Sauvegarde', icon: Database },
  { tab: 'settings', label: 'Paramètres', icon: Settings },
  { tab: 'admins', label: 'Administrateurs', icon: Shield },
];

const ROUTE_ITEMS = [
  { path: '/prospects', label: 'Prospects (Démo/Devis)', icon: MessageSquare, highlight: true },
];

export default function SuperAdminSidebar({ onClose, onLogout }) {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeTab = (location.pathname === '/admin' && (searchParams.get('tab') || 'overview')) || null;

  const initials = ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase() || 'SA';

  const isTabActive = (tab) => location.pathname === '/admin' && activeTab === tab;
  const isRouteActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="flex h-full w-[260px] flex-col bg-[#0B1437] text-slate-100" data-testid="superadmin-sidebar">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/[0.06]">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.6)]">
          <Shield className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white truncate">HR Engine</div>
          <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-violet-300">
            <Shield className="h-3 w-3" />
            COCKPIT SUPERADMIN
          </div>
        </div>
      </div>

      {/* Back to app */}
      <Link
        to="/dashboard"
        onClick={onClose}
        data-testid="back-to-app-link"
        className="mx-3 mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/[0.05] transition"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Retour à l'application
      </Link>

      {/* Cockpit nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/10">
        <div className="mb-2 px-3 text-[10px] font-semibold tracking-[0.14em] text-slate-500 uppercase">
          Pilotage plateforme
        </div>
        {TAB_ITEMS.map((it) => {
          const Icon = it.icon;
          const active = isTabActive(it.tab);
          return (
            <Link
              key={it.tab}
              to={`/admin${it.tab && it.tab !== 'overview' ? `?tab=${it.tab}` : ''}`}
              onClick={onClose}
              data-testid={`sa-nav-${it.tab}`}
              className={cn(
                'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
                active
                  ? 'bg-gradient-to-r from-violet-500/20 to-indigo-500/10 text-white shadow-[inset_0_0_0_1px_rgba(139,92,246,0.25)]'
                  : 'text-slate-300 hover:text-white hover:bg-white/[0.04]'
              )}
            >
              <Icon className={cn('h-4 w-4', active ? 'text-violet-300' : 'text-slate-400 group-hover:text-slate-200')} />
              <span className="flex-1 truncate">{it.label}</span>
              {it.highlight && !active && (
                <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300">NEW</span>
              )}
            </Link>
          );
        })}

        <div className="mt-6 mb-2 px-3 text-[10px] font-semibold tracking-[0.14em] text-slate-500 uppercase">
          Pages dédiées
        </div>
        {ROUTE_ITEMS.map((it) => {
          const Icon = it.icon;
          const active = isRouteActive(it.path);
          return (
            <Link
              key={it.path}
              to={it.path}
              onClick={onClose}
              data-testid={`sa-route-${it.path.replace(/\//g, '-')}`}
              className={cn(
                'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition',
                active
                  ? 'bg-gradient-to-r from-violet-500/20 to-indigo-500/10 text-white shadow-[inset_0_0_0_1px_rgba(139,92,246,0.25)]'
                  : 'text-slate-300 hover:text-white hover:bg-white/[0.04]'
              )}
            >
              <Icon className={cn('h-4 w-4', active ? 'text-violet-300' : 'text-slate-400 group-hover:text-slate-200')} />
              <span className="flex-1 truncate">{it.label}</span>
              {it.highlight && !active && (
                <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300">NEW</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className="border-t border-white/[0.06] p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04] transition-colors">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-700 text-xs font-bold text-white ring-1 ring-white/10">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="text-[11px] text-violet-300 truncate">SuperAdmin</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="superadmin-logout-btn"
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
